import React, { Suspense } from "react";
import Header from "./Header";
import AppRail from "./AppRail";
import ZoneSidebar from "./ZoneSidebar";
import { ZoneSidebarProvider } from "../context/ZoneSidebarContext";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import EntryPortal from "./EntryPortal";
import EntryCommerce from "./EntryCommerce";
import EntryAdmin from "./EntryAdmin";
import EntryActivity from "./EntryActivity";
import PaymentFromPayProGlobal from "./payment/payproglobal";
import Login from "./Login";
import LoginFromGoogle from "./LoginFromGoogle";
import LoginFromGoogleRedirectToBackend from "./LoginFromGoogleRedirectToBackend";
import Buy from "./buy";
import Register from "./Register";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import Preview from "./Preview";
import TermsOfService from "./TermsOfService";
import PrivacyPolicy from "./PrivacyPolicy";
import ContactUs from "./ContactUs";
import Invitation from "./Invitation";
import Accept from "./Accept";
import Setup from "./Setup";
import JoinCollection from "./JoinCollection";
import JoinMentor from "./JoinMentor";
import MyUserProfile from "./MyUserProfile";
import Portal from "./portal";
import HomePage from "./home";
import CapturePage from "./home/CapturePage";
import NotificationsPage from "./notifications/NotificationsPage";
import ReadOnlyViewWrapper from "./ReadOnlyViewWrapper";
import RunActivityPage from "./runActivity";
import RequireAuth from "./RequireAuth";
import RequireAuthWithRole from "./RequireAuthWithRole";
import ExampleCatalogue from "./examples/Catalogue";
import SciencePage from "./science";
import Marketplace from "./marketplace";
import CategoryPage from "./marketplace/CategoryPage";
import { AUTHOR_ROLE, EXPERT_ROLE, SYSADMIN_ROLE } from "../lib/roles";
import FloatingCompanion from "./companion/FloatingCompanion";
import CelebrationToast from "./companion/CelebrationToast";
import MobileBottomNav from "./MobileBottomNav";
import FeedbackWidget from "./feedback/FeedbackWidget";
import { useCompanion } from "../context/CompanionProvider";
import { PageLoader } from "./ui/page-loader";
import { useIsMobile } from "../hooks/use-mobile";
import useAuth from "../hooks/useAuth";
import useStartPendingKeep from "../hooks/useStartPendingKeep";

// Retry wrapper for lazy imports — retries once after a short delay on chunk load failure
function lazyWithRetry(importFn: () => Promise<any>) {
  return React.lazy(() =>
    importFn().catch((error: Error) => {
      // Wait briefly then retry once for stale-cache chunk errors
      return new Promise<any>((resolve) => setTimeout(resolve, 1500)).then(() =>
        importFn()
      );
    })
  );
}

// Lazy-loaded routes — only downloaded when the user navigates to them
const Commerce = lazyWithRetry(() => import("./commerce"));
const Admin = lazyWithRetry(() => import("./Admin"));
const TraitLibraryAdmin = lazyWithRetry(() => import("./admin/traitLibrary/TraitLibraryAdmin"));
const DesignViewWrapper = lazyWithRetry(() => import("./DesignViewWrapper"));
const Review = lazyWithRetry(() => import("./Review"));
const MentorSession = lazyWithRetry(() => import("./mentor/session"));
const MentorSegmentsPage = lazyWithRetry(() => import("./mentor/segments/MentorSegmentsPage"));
const SpacesView = lazyWithRetry(() => import("./spaces"));
const MarketplaceVendor = lazyWithRetry(() => import("./marketplace/vendor"));
const ProductDetail = lazyWithRetry(() => import("./marketplace/product"));
const SubscriptionDetail = lazyWithRetry(() => import("./marketplace/subscription"));
const MyAccount = lazyWithRetry(() => import("./account"));
const DataEventView = lazyWithRetry(() => import("./dataEvent"));
const DataReportView = lazyWithRetry(() => import("./dataEvent/Report"));
const DataReportEditor = lazyWithRetry(() => import("./dataEvent/ReportEditor"));
const ExampleManagementPage = lazyWithRetry(() => import("./examples/ExampleManagementPage"));
const UserProfile = lazyWithRetry(() => import("./UserProfile"));
const ActivitiesList = lazyWithRetry(() => import("./ActivitiesList"));
const Dashboard = lazyWithRetry(() => import("./Dashboard"));
const InsightsPage = lazyWithRetry(() =>
  import("./insights").then((m) => ({ default: m.InsightsPage }))
);
const ChatsPage = lazyWithRetry(() => import("./chats"));
const MentoringPage = lazyWithRetry(() => import("./mentoring"));
const MeetingBuilderPage = lazyWithRetry(() => import("./meetings/builder/MeetingBuilderPage"));
const MeetingRoom = lazyWithRetry(() => import("./meetings/runtime/MeetingRoom"));
const MeetingSummary = lazyWithRetry(() => import("./meetings/summary/MeetingSummary"));
const JoinMeeting = lazyWithRetry(() => import("./meetings/join/JoinMeeting"));
const ProgramsPage = lazyWithRetry(() => import("./programs/ProgramsPage"));
const ProgramDetailPage = lazyWithRetry(() => import("./programs/ProgramDetailPage"));
const PwfScreen = lazyWithRetry(() => import("./pwf/PwfScreen"));
const TranslationsPage = lazyWithRetry(() => import("./translations/TranslationsPage"));
const PlansPage = lazyWithRetry(() => import("./plans"));
const StartPage = lazyWithRetry(() => import("./start/StartPage"));
const CheckinPage = lazyWithRetry(() => import("./start/CheckinPage"));
const ProjectRoom = lazyWithRetry(() => import("./home/cockpit/ProjectRoom"));

// Redirect component to handle /share/activity -> /activities/run
const ShareActivityRedirect = () => {
  const location = useLocation();

  React.useEffect(() => {
    // Use window.location.replace for a clean navigation that doesn't interfere
    // with the activity component's lifecycle and welcome screen logic
    window.location.replace(`/activities/run${location.search}`);
  }, [location.search]);

  return null;
};

// Redirect component to handle /share/product -> /marketplace/product/:pid
const ShareProductRedirect = () => {
  const location = useLocation();

  React.useEffect(() => {
    // Extract pid from query params and redirect to marketplace product page
    const params = new URLSearchParams(location.search);
    const pid = params.get("pid");

    if (pid) {
      window.location.replace(`/marketplace/product/${pid}`);
    } else {
      // If no pid, redirect to marketplace home
      window.location.replace("/marketplace");
    }
  }, [location.search]);

  return null;
};

// Smart redirect component that remembers last visited page (PWA only)
const SmartHomeRedirect = () => {
  const { auth } = useAuth();
  const isLoggedIn = !!auth?.email && !!auth?.accessToken;
  const lastPath = localStorage.getItem("lastVisitedPath");

  // Check if app is running as PWA (installed app)
  const isPWA =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true; // iOS Safari

  // PWA smart redirect: return to last visited path
  if (isPWA && lastPath) {
    const shouldRedirectToLast =
      lastPath !== "/" &&
      !lastPath.startsWith("/login") &&
      !lastPath.startsWith("/register") &&
      !lastPath.startsWith("/forgotpassword") &&
      !lastPath.startsWith("/passwordReset") &&
      !lastPath.startsWith("/accept/") &&
      !lastPath.startsWith("/invitation") &&
      !lastPath.startsWith("/join-collection") &&
      !lastPath.startsWith("/join-mentor") &&
      !lastPath.startsWith("/auth/google");

    if (shouldRedirectToLast) {
      return <Navigate to={lastPath} replace />;
    }
  }

  // Authenticated users go to /home, guests see the guest homepage
  return <Navigate to="/home" replace />;
};

const RoutesWithHeader = () => {
  const location = useLocation();
  const { auth: startAuth } = useAuth();
  useStartPendingKeep(!!startAuth?.email && !!startAuth?.accessToken);
  const { pendingDiscovery, clearDiscovery } = useCompanion();
  const isMobile = useIsMobile();
  const hideHeaderRoutes = ["/activities/run"];

  const hideHeader =
    hideHeaderRoutes.includes(location.pathname) ||
    location.pathname.startsWith("/start");

  // /pwf keeps the rail on the hub and studio editor; only the immersive
  // learner surfaces (quest map, journey overview, journey walk) go full-canvas.
  const pwfImmersive =
    location.pathname.startsWith("/pwf") &&
    (location.search.includes("map=") ||
      location.search.includes("walk=") ||
      location.search.includes("overview="));

  const hideRail =
    hideHeader ||
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/register") ||
    location.pathname.startsWith("/design") ||
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/forgotpassword") ||
    location.pathname.startsWith("/passwordReset") ||
    location.pathname.startsWith("/setup") ||
    location.pathname.startsWith("/accept") ||
    location.pathname.startsWith("/auth/") ||
    location.pathname.startsWith("/review") ||
    location.pathname.startsWith("/readonly") ||
    location.pathname.startsWith("/entry/") ||
    location.pathname.startsWith("/preview") ||
    location.pathname.startsWith("/buy") ||
    location.pathname.startsWith("/start") ||
    location.pathname.startsWith("/terms") ||
    location.pathname.startsWith("/privacy") ||
    location.pathname.startsWith("/__contact_disabled__") ||
    location.pathname.startsWith("/invitation") ||
    location.pathname.startsWith("/join-collection") ||
    location.pathname.startsWith("/join-mentor") ||
    location.pathname.startsWith("/share/") ||
    location.pathname.startsWith("/payment") ||
    location.pathname.startsWith("/dataEvent") ||
    location.pathname.startsWith("/insights") ||
    location.pathname.startsWith("/users/") ||
    location.pathname.startsWith("/meeting/join/");

  // Track last visited page for PWA support
  React.useEffect(() => {
    // Don't track auth-related or temporary routes
    if (
      location.pathname !== "/" &&
      !location.pathname.startsWith("/login") &&
      !location.pathname.startsWith("/register") &&
      !location.pathname.startsWith("/forgotpassword") &&
      !location.pathname.startsWith("/passwordReset") &&
      !location.pathname.startsWith("/accept/") &&
      !location.pathname.startsWith("/invitation") &&
      !location.pathname.startsWith("/join-collection") &&
      !location.pathname.startsWith("/join-mentor") &&
      !location.pathname.startsWith("/auth/google") &&
      !location.pathname.startsWith("/share/")
    ) {
      localStorage.setItem("lastVisitedPath", location.pathname);
    }
  }, [location.pathname]);

  const hideZoneSidebar =
    location.pathname.startsWith("/marketplace/product/") ||
    location.pathname === "/home" ||
    location.pathname === "/programs" ||
    location.pathname.startsWith("/programs/");
  const showMobileNav = !hideRail && isMobile;
  // The public first-minute funnel stays quiet: no companion or feedback overlays.
  const hideFloatingWidgets = location.pathname.startsWith("/start");

  return (
    <>
      {!hideHeader && <Header />}
      <CelebrationToast celebration={pendingDiscovery} onDone={clearDiscovery} />
      {!hideFloatingWidgets && <FloatingCompanion />}
      {!hideFloatingWidgets && <FeedbackWidget />}
      <ZoneSidebarProvider>
      <div className="flex flex-1 overflow-hidden">
        {!hideRail && !isMobile && (
          <>
            <AppRail />
            {!hideZoneSidebar && <ZoneSidebar />}
          </>
        )}
        <main
          className="flex-1 flex flex-col overflow-y-auto min-w-0"
          style={showMobileNav ? { paddingBottom: "calc(56px + env(safe-area-inset-bottom, 0px))" } : undefined}
        >
          <Suspense fallback={<PageLoader label="Loading..." />}>
            <Routes>
            <Route path="/" element={<SmartHomeRedirect />} />
            <Route path="/entry/portal" element={<EntryPortal />} />
            <Route path="/entry/commerce" element={<EntryCommerce />} />
            <Route path="/entry/admin" element={<EntryAdmin />} />
            <Route path="/entry/activity" element={<EntryActivity />} />
            <Route
              path="/payment/payproglobal"
              element={<PaymentFromPayProGlobal />}
            />
            <Route path="/login" element={<Login />} />
            <Route path="/loginfromgoogle" element={<LoginFromGoogle />} />
            <Route
              path="/auth/google/callback"
              element={<LoginFromGoogleRedirectToBackend />}
            />
            <Route path="/register" element={<Register />} />
            <Route path="/forgotpassword" element={<ForgotPassword />} />
            <Route path="/passwordReset" element={<ResetPassword />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/preview" element={<Preview />} />
            <Route path="/readonly" element={<ReadOnlyViewWrapper />} />
            <Route path="/accept/:id" element={<Accept />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route
              path="/marketplace/category/:categorySlug"
              element={<CategoryPage />}
            />
            <Route
              path="/marketplace/vendor/:vendorId"
              element={<MarketplaceVendor />}
            />
            <Route
              path="/marketplace/product/:productId"
              element={<ProductDetail />}
            />
            <Route
              path="/marketplace/subscription/:productId"
              element={<SubscriptionDetail />}
            />
            <Route path="/examples" element={<ExampleCatalogue />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/start" element={<StartPage />} />
            <Route path="/start/:seedId" element={<StartPage />} />
            <Route path="/science" element={<SciencePage />} />
            <Route path="/spaces" element={<SpacesView />} />
            <Route path="/invitation" element={<Invitation />} />
            <Route path="/join-collection" element={<JoinCollection />} />
            <Route path="/join-mentor/:token" element={<JoinMentor />} />
            <Route element={<RequireAuth />}>
              {/* Waits for auth hydration, then redirects to /login with state.from so login returns here. */}
              <Route path="/start/checkin/:flowId" element={<CheckinPage />} />
              <Route path="/capture" element={<CapturePage />} />
              <Route path="/project/:id" element={<ProjectRoom />} />
              <Route path="/chats" element={<ChatsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/buy" element={<Buy />} />
              <Route path="/setup" element={<Setup />} />
              <Route path="/portal" element={<Portal />} />
              <Route path="/review" element={<Review />} />
              <Route
                path="/examples/:activityId"
                element={<ExampleManagementPage />}
              />
              <Route path="/profile" element={<MyUserProfile />} />
              <Route path="/commerce" element={<Commerce />} />
              <Route path="/account" element={<MyAccount />} />
              <Route path="/dataEvent" element={<DataEventView />} />
              <Route path="/dataEvent/report" element={<DataReportView />} />
              <Route
                path="/dataEvent/reportEditor"
                element={<DataReportEditor />}
              />
              <Route path="/insights" element={<InsightsPage />} />
              <Route path="/programs" element={<ProgramsPage />} />
              <Route path="/programs/:id" element={<ProgramDetailPage />} />
              <Route path="/translations" element={<TranslationsPage />} />
            </Route>
            <Route element={<RequireAuthWithRole role={AUTHOR_ROLE} />}>
              <Route path="/design" element={<DesignViewWrapper />} />
            </Route>
            <Route element={<RequireAuthWithRole role={EXPERT_ROLE} />}>
              <Route path="/mentor/session" element={<MentorSession />} />
              <Route path="/mentor/segments" element={<MentorSegmentsPage />} />
              <Route path="/mentor/segments/:id" element={<MentorSegmentsPage />} />
              <Route path="/mentoring" element={<MentoringPage />} />
              <Route path="/meetings/builder" element={<MeetingBuilderPage />} />
              <Route path="/meetings/builder/:templateId" element={<MeetingBuilderPage />} />
            </Route>
            <Route path="/meetings/:meetingId" element={<MeetingRoom />} />
            <Route path="/meetings/:meetingId/summary" element={<MeetingSummary />} />
            <Route element={<RequireAuthWithRole role={SYSADMIN_ROLE} />}>
              <Route path="/admin" element={<Admin />} />
              <Route path="/users/:id" element={<UserProfile />} />
              <Route path="/admin/activities" element={<ActivitiesList />} />
              <Route path="/admin/trait-library" element={<TraitLibraryAdmin />} />
            </Route>
            <Route path="/pwf" element={<PwfScreen />} />
            <Route path="/share/activity" element={<ShareActivityRedirect />} />
            <Route path="/share/product" element={<ShareProductRedirect />} />
            <Route path="/activities/run" element={<RunActivityPage />} />
            <Route path="/meeting/join/:token" element={<JoinMeeting />} />
            <Route path="/meetings/:meetingId/client" element={<MeetingRoom />} />
            </Routes>
          </Suspense>
        </main>
      </div>
      </ZoneSidebarProvider>
      {showMobileNav && <MobileBottomNav />}
    </>
  );
};

export default RoutesWithHeader;
